import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, Sparkles, Loader2, ShoppingBag, ExternalLink, MessageCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API_BASE, api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { whatsappLinks } from "@/lib/whatsapp";

const SESSION_KEY = "hdh_chat_session";
const SUGGESTIONS = [
  "Welche Hülle passt für mein iPhone 16 Pro?",
  "Was kostet ein Display-Tausch beim Samsung S25 Ultra?",
  "Welche Powerbank empfehlt ihr für unterwegs?",
  "Habt ihr AirPods Pro auf Lager?",
];

const PRODUCTS_RE = /\[\[PRODUCTS:([\w,-]+)\]\]/g;

// Lightweight markdown renderer config — strips outer paragraph wrappers and keeps it compact
const MD_COMPONENTS = {
  p: ({ children }) => <span className="block mb-1.5 last:mb-0">{children}</span>,
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-0.5 mb-1.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-0.5 mb-1.5">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  a: ({ href, children }) => <a href={href} className="text-[#FF2D40] underline underline-offset-2 hover:text-white" target="_blank" rel="noreferrer">{children}</a>,
  code: ({ children }) => <code className="px-1 py-0.5 rounded bg-white/10 text-[12px]">{children}</code>,
  h1: ({ children }) => <span className="block font-display font-semibold text-white mb-1">{children}</span>,
  h2: ({ children }) => <span className="block font-display font-semibold text-white mb-1">{children}</span>,
  h3: ({ children }) => <span className="block font-display font-semibold text-white mb-1">{children}</span>,
};

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hallo! 👋 Ich bin dein Shopping- & Service-Assistent von Haus des Handys. Wobei kann ich dir helfen — Zubehör, Reparatur oder Beratung?",
      products: [],
    },
  ]);
  const [productCache, setProductCache] = useState({});
  const scrollRef = useRef(null);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  // Allow other components (e.g. MobileActionBar) to open the chat via a custom event
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("hdh:open-ai-chat", onOpen);
    return () => window.removeEventListener("hdh:open-ai-chat", onOpen);
  }, []);

  const fetchProduct = async (sku) => {
    if (productCache[sku]) return productCache[sku];
    try {
      const r = await api.get(`/products/${sku}`);
      setProductCache((c) => ({ ...c, [sku]: r.data }));
      return r.data;
    } catch { return null; }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, products: [] }, { role: "assistant", content: "", products: [] }]);
    setLoading(true);

    let buffer = "";
    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              setMessages((m) => {
                const cp = [...m];
                cp[cp.length - 1] = { role: "assistant", content: "Entschuldige, momentan ein Verbindungsproblem. Versuch es bitte gleich nochmal.", products: [] };
                return cp;
              });
              continue;
            }
            if (parsed.t) {
              buffer += parsed.t;
              const cleanText = buffer.replace(PRODUCTS_RE, "").trim();
              setMessages((m) => {
                const cp = [...m];
                cp[cp.length - 1] = { ...cp[cp.length - 1], content: cleanText };
                return cp;
              });
            }
          } catch { /* skip malformed line */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const cp = [...m];
        cp[cp.length - 1] = { role: "assistant", content: "Verbindungsproblem. Bitte versuche es nochmal.", products: [] };
        return cp;
      });
    } finally {
      const tagMatch = [...buffer.matchAll(PRODUCTS_RE)];
      if (tagMatch.length > 0) {
        const skus = tagMatch[tagMatch.length - 1][1].split(",").map((s) => s.trim()).filter(Boolean);
        const products = (await Promise.all(skus.map(fetchProduct))).filter(Boolean);
        setMessages((m) => {
          const cp = [...m];
          cp[cp.length - 1] = { ...cp[cp.length - 1], products };
          return cp;
        });
      }
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-20 right-4 sm:right-6 z-40 group hidden md:block ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        data-testid="open-chat-fab"
      >
        <div className="absolute inset-0 bg-[#DC1928] rounded-full blur-2xl opacity-60 group-hover:opacity-90 transition-opacity"></div>
        <div className="relative flex items-center gap-2 bg-white text-[#0A0A0B] pl-3 pr-5 py-3 rounded-full shadow-2xl btn-lift">
          <div className="relative">
            <Sparkles className="w-5 h-5 text-[#DC1928]" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          </div>
          <span className="text-sm font-semibold">AI Assistent</span>
        </div>
      </button>

      {open && (
        <div className="fixed bottom-20 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[420px] h-[640px] max-h-[calc(100vh-7rem)] bg-[#0F0F11] rounded-3xl shadow-2xl border border-white/10 flex flex-col overflow-hidden fade-up" data-testid="chat-panel">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between bg-gradient-to-b from-[#1a0509] to-transparent">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 rounded-full bg-[#DC1928] flex items-center justify-center glow-red-sm">
                <Sparkles className="w-5 h-5 text-white" />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#0F0F11]"></span>
              </div>
              <div>
                <div className="font-display font-semibold text-white text-sm">HdH Assistent</div>
                <div className="text-[11px] text-emerald-400">Online · GPT-4o-mini</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-2 hover:bg-white/5 rounded-full text-zinc-400" data-testid="close-chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i}>
                <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-[14px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#DC1928] text-white rounded-2xl rounded-br-md whitespace-pre-wrap"
                        : "bg-[#1C1C20] text-zinc-100 rounded-2xl rounded-bl-md border border-white/5"
                    }`}
                  >
                    {m.role === "assistant" ? (
                      m.content ? (
                        <ReactMarkdown components={MD_COMPONENTS}>{m.content}</ReactMarkdown>
                      ) : (
                        loading && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : null
                      )
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
                {m.products && m.products.length > 0 && (
                  <div className="mt-2.5 space-y-2 max-w-[90%]" data-testid={`chat-products-${i}`}>
                    {m.products.map((p) => <ChatProductCard key={p.sku} product={p} onClose={() => setOpen(false)} />)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {messages.length <= 1 && (
            <div className="px-5 pb-3 flex flex-wrap gap-2">
              {SUGGESTIONS.slice(0, 3).map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 hover:border-[#DC1928] hover:text-white transition-colors"
                  data-testid={`chat-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="px-4 py-3 border-t border-white/5 flex items-center gap-2 bg-[#0A0A0B]"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Frag mich nach Produkten oder Reparaturen..."
              className="flex-1 bg-[#1C1C20] border border-white/5 rounded-full px-4 py-2.5 text-sm outline-none text-white placeholder-zinc-500 focus:border-[#DC1928] focus:ring-2 focus:ring-[#DC1928]/20"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#DC1928] text-white flex items-center justify-center disabled:opacity-40 btn-lift hover:bg-[#B01829]"
              data-testid="chat-send"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function ChatProductCard({ product: p, onClose }) {
  const { add } = useCart();
  return (
    <div className="bg-[#141417] border border-white/10 rounded-2xl overflow-hidden hover:border-[#DC1928]/40 transition-colors" data-testid={`chat-product-${p.sku}`}>
      <div className="flex">
        <div className="w-24 h-24 bg-[#0A0A0B] shrink-0">
          <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 p-3 min-w-0">
          <div className="font-display font-semibold text-white text-sm leading-snug line-clamp-2">{p.name}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{p.compatible}</div>
          <div className="flex items-center justify-between mt-2">
            <div className="font-display font-bold text-white numeric">€{p.price.toFixed(2)}</div>
            {p.stock > 0 && <span className="text-[10px] text-emerald-400">Auf Lager</span>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 border-t border-white/5">
        <Link
          to={`/produkt/${p.sku}`}
          onClick={onClose}
          className="py-2.5 text-xs font-medium text-zinc-300 hover:text-white hover:bg-white/5 flex items-center justify-center gap-1.5 transition-colors border-r border-white/5"
          data-testid={`chat-view-${p.sku}`}
        >
          <ExternalLink className="w-3 h-3" /> Ansehen
        </Link>
        <button
          onClick={() => add(p, 1)}
          className="py-2.5 text-xs font-semibold text-white bg-[#DC1928]/15 hover:bg-[#DC1928]/30 flex items-center justify-center gap-1.5 transition-colors border-r border-white/5"
          data-testid={`chat-cart-${p.sku}`}
        >
          <ShoppingBag className="w-3 h-3" /> Warenkorb
        </button>
        {(() => {
          const wa = whatsappLinks.product({ name: p.name, price: p.price, compatible: p.compatible, sku: p.sku });
          return (
            <a
              href={wa.href}
              onClick={wa.onClick}
              target="_blank"
              rel="noreferrer"
              className="py-2.5 text-xs font-semibold text-white bg-[#25D366]/20 hover:bg-[#25D366]/40 flex items-center justify-center gap-1.5 transition-colors"
              data-testid={`chat-wa-${p.sku}`}
            >
              <MessageCircle className="w-3 h-3" /> WhatsApp
            </a>
          );
        })()}
      </div>
    </div>
  );
}
